import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Package } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article8 = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.backToHome')}
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge className="bg-orange-600 text-white mr-4">
              <Package className="h-3 w-3 mr-1" />
              {t('category.ecommerce')}
            </Badge>
            <span className="text-sm text-gray-600">Amazon</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            January 8, 2026
            <span className="mx-2">•</span>
            <span>7 min read</span>
          </div>
          
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.8.title')}
          </h1>
          
          <img 
            src="https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=1200&auto=format&fit=crop&q=80" 
            alt="Amazon robotics and automation"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.8.intro')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.8.section1.title')}</h2>
            
            <p className="mb-6">
              {t('article.8.section1.content')}
            </p>

            <p className="mb-6">
              {t('article.8.detail1')}
            </p>

            <p className="mb-6">
              {t('article.8.detail2')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.8.section2.title')}</h2>
            
            <p className="mb-6">
              {t('article.8.section2.content')}
            </p>

            <p className="mb-6">
              {t('article.8.detail3')}
            </p>

            <p className="mb-6">
              {t('article.8.detail4')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.8.section3.title')}</h2>
            
            <p className="mb-6">
              {t('article.8.section3.content')}
            </p>

            <p className="mb-6">
              {t('article.8.detail5')}
            </p>

            <p className="mb-6">
              {t('article.8.detail6')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.8.section4.title')}</h2>
            
            <p className="mb-6">
              {t('article.8.section4.content')}
            </p>

            <p className="mb-6">
              {t('article.8.detail7')}
            </p>

            <p className="mb-6">
              {t('article.8.detail8')}
            </p>

            <div className="bg-orange-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-gray-900 mb-2">Amazon Robotics Innovation Areas</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li><strong>Autonomous Mobile Robots:</strong> Warehouse navigation and transport</li>
                <li><strong>Robotic Arms:</strong> Picking, packing, and sorting operations</li>
                <li><strong>Intelligent Sorting:</strong> AI-powered package classification</li>
                <li><strong>Delivery Automation:</strong> Drones and autonomous delivery vehicles</li>
                <li><strong>Smart Packaging:</strong> Adaptive packaging systems</li>
                <li><strong>Predictive Maintenance:</strong> Machine learning optimization</li>
              </ul>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Amazon official press releases.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a 
              href="https://www.therobotreport.com/amazon-acquires-rightbot-adds-robotics-delivery-packaging-innovation-team/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToAllArticles')}
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article8;